import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { CoreAppState } from '../core-app.state';
import { MessageService } from '../../services/message.service';
import { ThreadService } from '../../services/thread.service';
import { chatActions } from './chat.actions';
import { firstValueFrom, map, mergeMap, of, switchMap } from 'rxjs';
import { ChatService } from '../../services/chat.service';
import { DateTime } from 'luxon';
import { NoAction } from '../base-resource.effects';
import { selectChat } from './chat.reducer';
import { selectAuth } from '../auth/auth.reducer';

@Injectable()
export class ChatEffects {
  // create new thread
  createNewThread$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chatActions.createNewThread),
      mergeMap((payload) => {
        return this.threadService
          .create({
            orderCardId: payload.orderCardId,
            customerProfileId: payload.customerProfileId,
          })
          .pipe(
            map((res) => {
              if (payload.initialMessage) {
                this.chatService.sendMessage({
                  ...payload.initialMessage,
                  threadId: res.id,
                });
              }
              return chatActions.connectToThread({ threadId: res.id });
            }),
          );
      }),
    ),
  );

  // connect to thread
  connectToThread$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chatActions.connectToThread),
      mergeMap((payload) => {
        this.chatService.connectToChannel(payload.threadId);

        return this.threadService
          .getOne(payload.threadId, {
            customerProfile: true,
            orderCard: {
              include: {
                order: {
                  include: {
                    customerProfile: true,
                  },
                },
              },
            },
          })
          .pipe(
            switchMap((threadRes) =>
              this.messageService
                .getMany({
                  where: {
                    threadId: payload.threadId,
                  },
                  include: {
                    customerProfile: true,
                    staffProfile: true,
                  },
                })
                .pipe(
                  map((messagesRes) =>
                    chatActions.threadConnected({
                      messages: messagesRes.data,
                      thread: threadRes,
                    }),
                  ),
                ),
            ),
          );
      }),
    ),
  );

  // connect to thread for order card
  connectToThreadForOrderCard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chatActions.tryConnectToThreadForOrderCard),
      mergeMap((payload) => {
        return this.threadService
          .getMany({
            where: {
              orderCardId: payload.orderCardId,
            },
          })
          .pipe(
            map((res) =>
              res?.data?.length >= 0
                ? chatActions.connectToThread({
                    threadId: res.data[0]?.id,
                  })
                : NoAction(),
            ),
          );
      }),
    ),
  );

  // connect to thread for customer profile id
  connectToThreadForCustomerProfileId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chatActions.tryConnectToThreadForCustomer),
      mergeMap((payload) => {
        return this.threadService
          .getMany({
            where: {
              customerProfileId: payload.customerId,
            },
          })
          .pipe(
            map((res) =>
              res?.data?.length >= 0
                ? chatActions.connectToThread({
                    threadId: res.data[0]?.id,
                  })
                : NoAction(),
            ),
          );
      }),
    ),
  );

  // connect to staff threads
  connectToStaffThreads$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chatActions.connectToStaffThreads),
      switchMap(() =>
        this.threadService
          .getMany({
            include: {
              customerProfile: true,
              orderCard: {
                include: {
                  order: {
                    include: {
                      customerProfile: true,
                    },
                  },
                },
              },
              messages: {
                orderBy: {
                  sentAt: 'desc',
                },
                take: 10,
              },
            },
          })
          .pipe(
            map((res) => {
              for (const thread of res.data ?? []) {
                this.chatService.connectToChannel(thread.id);
              }
              return chatActions.threadsConnected({
                threads: res.data ?? [],
              });
            }),
          ),
      ),
    ),
  );
  // connect to customer threads
  connectToCustomerThreads$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chatActions.connectToCustomerThreads),
      switchMap(() =>
        this.store
          .select((s) => selectAuth(s).user)
          .pipe(
            switchMap((user) =>
              user
                ? this.threadService
                    .getMany({
                      where: {
                        OR: [
                          {
                            orderCard: {
                              order: {
                                customerProfileId: user.customerProfileId!,
                              },
                            },
                          },
                          {
                            customerProfileId: user.customerProfileId,
                          },
                        ],
                      },
                      include: {
                        orderCard: {
                          include: {
                            order: {
                              include: {
                                customerProfile: true,
                              },
                            },
                          },
                        },
                        messages: {
                          orderBy: {
                            sentAt: 'desc',
                          },
                          take: 10,
                        },
                      },
                    })
                    .pipe(
                      map((res) => {
                        for (const thread of res.data ?? []) {
                          this.chatService.connectToChannel(thread.id);
                        }
                        return chatActions.threadsConnected({
                          threads: res.data ?? [],
                        });
                      }),
                    )
                : of(NoAction()),
            ),
          ),
      ),
    ),
  );

  // send message
  sendMessage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(chatActions.sendMessage),
        map((payload) => {
          this.chatService.sendMessage(payload.data);
        }),
      ),
    {
      dispatch: false,
    },
  );

  // mark message as read
  markMessagesAsRead$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(chatActions.markMessagesAsRead),
        map((payload) => {
          for (const messageId of payload.messageIds) {
            this.chatService.updateMessage(messageId, {
              readAt: DateTime.now().toJSDate(),
            });
          }
        }),
      ),
    {
      dispatch: false,
    },
  );

  // disconnect from thread
  disconnectFromThread$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(chatActions.disconnectFromThread),
        map((payload) => {
          this.chatService.disconnectFromChannel(payload.threadId);
        }),
      ),
    {
      dispatch: false,
    },
  );

  constructor(
    private readonly actions$: Actions,
    private readonly store: Store<CoreAppState>,
    private readonly messageService: MessageService,
    private readonly threadService: ThreadService,
    private readonly chatService: ChatService,
  ) {
    this.chatService.reset$.subscribe(() => {
      // console.log('RESET NOW');
    });
    this.chatService.reset$.subscribe(async () => {
      const activeThreads = await firstValueFrom(
        this.store.select((s) => selectChat(s).activeThreads),
      );
      for (const activeThread of activeThreads) {
        this.store.dispatch(
          chatActions.connectToThread({ threadId: activeThread.thread.id }),
        );
      }
    });
    this.chatService.newMessage$.subscribe((newMessage) => {
      this.store.dispatch(
        chatActions.messageAdded({
          message: newMessage,
          threadId: newMessage.threadId,
        }),
      );
    });
    this.chatService.messageUpdated$.subscribe((message) => {
      this.store.dispatch(
        chatActions.messageUpdated({
          message: message,
          threadId: message.threadId,
        }),
      );
    });
  }
}
